import { EMPTY, Observable, catchError, exhaustMap, switchMap, tap } from 'rxjs';
import { inject } from '@angular/core';
import { toastEvents } from '@navstation-portal/shared/data-access/toaster';
import { signalStoreFeature } from '@ngrx/signals';
import { Dispatcher, Events, injectDispatch, withEventHandlers } from '@ngrx/signals/events';
import { formRuleDebugEvents } from './form-rule-debug-events';
import { REPORT_DEBUG_API_SERVICE } from './report-debug-dependencies';
import { reportEvents } from './report-events';

// DEV-ONLY form-rule debug. Delete this feature with withFormRuleDebugReducer
// and formRuleDebugEvents to remove the form-rule debug UI.
//
// The debug service is optional. An app that provides none gets no handler, so
// the two load events do nothing there.
export function withFormRuleDebugEventHandlers() {
  return signalStoreFeature(
    withEventHandlers((): Record<string, Observable<unknown>> => {
      const debugApi = inject(REPORT_DEBUG_API_SERVICE, { optional: true });
      if (debugApi === null) {
        return {};
      }

      const events = inject(Events);
      const dispatcher = inject(Dispatcher);
      const toastDispatcher = injectDispatch(toastEvents);

      const showError = (summary: string) => (error: Error) => {
        toastDispatcher.toastError({ summary, detail: error.message });
        return EMPTY;
      };

      return {
        // The names do not change while the page is open, so a second click
        // while they load starts no second request.
        loadTemplateNames$: events.on(formRuleDebugEvents.loadTemplateNames).pipe(
          exhaustMap(() =>
            debugApi.getFormRuleTemplateNames().pipe(
              tap((names) => dispatcher.dispatch(formRuleDebugEvents.gotTemplateNames(names))),
              catchError(showError('Template names failed'))
            )
          )
        ),
        // Each click asks for a new merge, so only the last one reaches the
        // form. The same gotFormRuleTemplate event as the load path applies
        // it, so no other code knows the debug UI exists.
        applyTemplateOverride$: events.on(formRuleDebugEvents.applyTemplateOverride).pipe(
          switchMap(({ payload: { names, vesselId, applyVesselOverride } }) =>
            debugApi.getMergedFormRuleTemplate(names, vesselId, applyVesselOverride).pipe(
              tap((template) => {
                dispatcher.dispatch(reportEvents.gotFormRuleTemplate(template));
                dispatcher.dispatch(formRuleDebugEvents.setTemplateOverride(names));
              }),
              catchError(showError('Template merge failed'))
            )
          )
        ),
      };
    })
  );
}
